/**
 * Array diff — given previous and next arrays of entities, compute the
 * set of add / update / delete operations needed to transition between them.
 *
 * Used by the dual-backed usePersistentMockState hook to translate
 * "setRows(newArray)" calls into a batched InstantDB transact.
 *
 * Identity: entities are matched by their `_id` field (the legacy mock id).
 * The `id` (InstantDB UUID) is tracked separately in an idMap so we always
 * know which UUID corresponds to which `_id`.
 *
 * Performance: O(n + m) where n = prev.length and m = next.length.
 * Equality check uses JSON.stringify with a BigInt-aware replacer, which is
 * fine for typical row sizes (hundreds to a few thousand bytes).
 */

type EntityWithId = { _id: string } & Record<string, unknown>;

export type EntityDiff<T extends EntityWithId> = {
  added: T[];
  updated: T[];
  removed: T[];
  unchanged: T[];
};

/** Stable JSON with BigInt → "BI:..." tagging (mirrors demoStore). */
function stringify(value: unknown): string {
  return JSON.stringify(value, (_k, v) =>
    typeof v === "bigint" ? `BI:${v.toString()}` : v,
  );
}

/**
 * Diff two arrays of entities by their `_id` field.
 *
 * Items in `next` but not in `prev` → added.
 * Items in both, with any field changed → updated.
 * Items in `prev` but not in `next` → removed.
 * Items in both, identical → unchanged (returned but not used by callers).
 */
export function diffArrays<T extends EntityWithId>(
  prev: ReadonlyArray<T>,
  next: ReadonlyArray<T>,
): EntityDiff<T> {
  const prevMap = new Map<string, T>();
  for (const item of prev) {
    prevMap.set(item._id, item);
  }

  const added: T[] = [];
  const updated: T[] = [];
  const unchanged: T[] = [];
  const seenIds = new Set<string>();

  for (const item of next) {
    seenIds.add(item._id);
    const prior = prevMap.get(item._id);
    if (!prior) {
      added.push(item);
    } else if (stringify(prior) !== stringify(item)) {
      updated.push(item);
    } else {
      unchanged.push(item);
    }
  }

  const removed: T[] = [];
  for (const item of prev) {
    if (!seenIds.has(item._id)) {
      removed.push(item);
    }
  }

  return { added, updated, removed, unchanged };
}
